import { useEffect, useRef, useState } from "react";
import type { ProjectChapter } from "@/mocks/gallery";

interface ChapterBlockProps {
  chapter: ProjectChapter;
  index: number;
}

/**
 * Alternating editorial chapter block.
 *
 * - Large image on one side, story on the other, mirrors on odd indexes
 * - Image has a subtle parallax + reveal-on-scroll fade
 * - Hover on the image reveals a soft warm inner glow, like touching light
 */
export default function ChapterBlock({ chapter, index }: ChapterBlockProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [parallax, setParallax] = useState(0);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setVisible(true);
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);

    const onScroll = () => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const winH = window.innerHeight;
      // -1 = just entering from bottom, 1 = leaving top
      const t = (rect.top + rect.height / 2 - winH / 2) / winH;
      setParallax(Math.max(-1, Math.min(1, t)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const flipped = chapter.alignment === "right";

  return (
    <section
      ref={wrapperRef}
      className="relative w-full py-24 md:py-40 px-6 md:px-14 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Story column */}
        <div
          className={`lg:col-span-5 ${flipped ? "lg:order-2" : "lg:order-1"}`}
          style={{
            opacity: visible ? 1 : 0,
            transform: `translateY(${visible ? 0 : 30}px)`,
            transition: "opacity 900ms ease-out, transform 900ms ease-out",
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.45em] text-white/50 mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-white/40" />
            {chapter.eyebrow}
          </div>
          <h2
            className="font-black uppercase leading-[0.95] tracking-[-0.02em] text-white mb-8"
            style={{ fontSize: "clamp(32px, 3.6vw, 56px)" }}
          >
            {chapter.heading}
          </h2>
          <p
            className="text-white/75 font-light leading-[1.7]"
            style={{ fontSize: "clamp(15px, 1.15vw, 19px)" }}
          >
            {chapter.body}
          </p>
        </div>

        {/* Image column — larger, interactive */}
        <div
          className={`lg:col-span-7 ${flipped ? "lg:order-1" : "lg:order-2"}`}
          style={{
            opacity: visible ? 1 : 0,
            transform: `translateY(${visible ? 0 : 40}px)`,
            transition: "opacity 1100ms ease-out 100ms, transform 1100ms ease-out 100ms",
          }}
        >
          <div
            className="relative overflow-hidden rounded-lg bg-black cursor-pointer group"
            style={{ aspectRatio: "16 / 11" }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <img
              src={chapter.image}
              alt={chapter.imageAlt}
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] ease-out"
              style={{
                transform: `scale(${hovered ? 1.06 : 1.02}) translateY(${parallax * -18}px)`,
                willChange: "transform",
              }}
            />
            {/* Warm hover wash — feels like touching light */}
            <div
              className="absolute inset-0 pointer-events-none transition-opacity duration-700"
              style={{
                opacity: hovered ? 1 : 0,
                background:
                  "radial-gradient(circle at 50% 55%, rgba(255,180,110,0.18) 0%, rgba(255,140,80,0.05) 40%, rgba(0,0,0,0) 75%)",
                mixBlendMode: "screen",
              }}
            />
            {/* Static bottom gradient for caption legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

            {/* Index marker */}
            <div className="absolute top-5 left-5 flex items-center gap-2 pointer-events-none">
              <span className="w-8 h-8 rounded-full border border-white/50 flex items-center justify-center text-white/80 text-[11px] font-mono">
                {String(index + 1).padStart(2, "0")}
              </span>
            </div>

            {/* Bottom caption */}
            <div className="absolute bottom-5 left-5 right-5 pointer-events-none">
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/70">
                {chapter.imageAlt}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}